import { addLogLine, getAllLogLines, clearLogLines } from './log';
import { supplierMapping } from './suppliers';
import { initPotentialSession } from './initiateSession';
import { notifyCompletion } from './notifyCompletion';
import { UserSessionParams, normalizeUserSessionParams } from './userSessionParams';
import { sendLogsToVerificationServer } from './reportLogs';
import Mdn from '../shared/mdn';
import { SupplierCode } from './suppliers/supplierCodes';

const IMPLIED_CONSENT_ID = 'IMPLIED CONSENT';

export enum VerificationFlowStatus {
    CHOSE_NOT_TO_VERIFY = 1,
    VERIFICATION_PROCEDURE_COMPLETED_SUCCESSFULLY = 2,
    VERIFICATION_PROCEDURE_ERROR = 3,
}

// No success because the client does not know if the verification was successful
type VerificationFlowResult = {
    status: VerificationFlowStatus,
    errorString?: string,
    logLines?: string[],
}

// This function is not safe, meaning that errors might be thrown and not caught here - use safeExecuteMobileVerificationFlow instead
async function executeMobileVerification(userSessionParams: UserSessionParams) : Promise<VerificationFlowResult> {
    addLogLine("started executeMobileVerification, userSessionParams: " + JSON.stringify(userSessionParams));
    
    const normalizedUserSessionParams = normalizeUserSessionParams(userSessionParams);
    if (!normalizedUserSessionParams) {
        addLogLine('Invalid userSessionParams! Doing nothing');
        return {
            status: VerificationFlowStatus.VERIFICATION_PROCEDURE_ERROR,
            errorString: 'Invalid userSessionParams',
        };
    }
    const { userId, siteId, extVersion, mdnObj, priceString } = normalizedUserSessionParams;


    const consentId = IMPLIED_CONSENT_ID;
    const consentTimestamp = new Date().toISOString();
    addLogLine('Generated consentId: ' + consentId);

    // This can throw an error and the error will be caught in the catch block
    let initSessionResObj = await initPotentialSession(userId, siteId, extVersion, consentId, consentTimestamp, mdnObj, priceString);
    addLogLine('initSessionResObj: ' + JSON.stringify(initSessionResObj));
    if (!initSessionResObj.sv) {
        addLogLine('initSession returned sv false - doing nothing');
        return {
            status: VerificationFlowStatus.CHOSE_NOT_TO_VERIFY
        };
    }
    const verificationParams = initSessionResObj.verificationParams;
    if (!verificationParams) {
        const errMsg = 'initSession returned sv true but no verificationParams - This is unexpected behavior!';
        addLogLine(errMsg);
        throw new Error(errMsg);
    }
    const { sessionId, supplierType } = verificationParams;
    addLogLine('Decided to verify, got sessionId: ' + sessionId + ' and supplierType: ' + supplierType);

    // we verify that the supplierType is in SupplierCode in initPotentialSession
    const fetchSupplierServerFunc = supplierMapping[supplierType as SupplierCode].fetchSupplierServerFunc;
    let supplierResponeObj;
    let possibleSupplierInitializationError: string = '';
    // We wrap this with a try catch because we want to notify the server even if the supplier fails
    try {
        const supplierInitializationResObj = await fetchSupplierServerFunc(userId, siteId, extVersion, consentId, consentTimestamp, sessionId, mdnObj);
        addLogLine('supplierInitializationResObj: ' + JSON.stringify(supplierInitializationResObj));
        supplierResponeObj = supplierInitializationResObj.supplierResponeObj;
    } catch (error) {
        addLogLine('Error in fetchSupplierServerFunc: ' + error);
        supplierResponeObj = {};
        possibleSupplierInitializationError = 'Error in fetchSupplierServerFunc: ' + error;
    }

    // Notifying server after getting response - whether true or false or error
    const notifyCompletionResultObject = await notifyCompletion(userId, siteId, extVersion, consentId, consentTimestamp, mdnObj, sessionId, supplierType, supplierResponeObj);
    if (!notifyCompletionResultObject) {
        const errMessage = 'notifyCompletion failed, no valid result object';
        addLogLine(errMessage);
        return {
            status: VerificationFlowStatus.VERIFICATION_PROCEDURE_ERROR,
            errorString: possibleSupplierInitializationError ? possibleSupplierInitializationError + '\n' + errMessage : errMessage,
        };
    } else {
        addLogLine('notifyCompletion succeeded, result object: ' + JSON.stringify(notifyCompletionResultObject));
        return {
            status: VerificationFlowStatus.VERIFICATION_PROCEDURE_COMPLETED_SUCCESSFULLY,
            errorString: possibleSupplierInitializationError,
        };
    }
}

export async function safeExecuteMobileVerification(userId: string, siteId: string, extVersion: string, mdnObj: Mdn, priceString: string) : Promise<VerificationFlowResult> {
    let verificationFlowResult: VerificationFlowResult;
    try {
        const userSessionParams: UserSessionParams = {
            userId,
            siteId,
            extVersion,
            mdnObj,
            priceString,
        };
        verificationFlowResult = await executeMobileVerification(userSessionParams);

        if (!verificationFlowResult) {
            throw new Error("verificationFlowResult is null");
        }

    } catch (error) {
        addLogLine('Error in safeExecuteVerificationFlow: ' + error);
        verificationFlowResult = {
            status: VerificationFlowStatus.VERIFICATION_PROCEDURE_ERROR,
            errorString: 'Error in safeExecuteVerificationFlow: ' + error,
        };
    }
    // finally
    verificationFlowResult.logLines = getAllLogLines();
    clearLogLines();
    sendLogsToVerificationServer(verificationFlowResult.logLines);
    return verificationFlowResult;
}
